import { useQuery } from '@tanstack/react-query';
import useUserProfile from './useUserProfile';
import { client } from '@util/getTypesense';
import { ProductDocument } from 'models/product';
import { MultiSearchRequestSchema } from 'typesense/lib/Typesense/MultiSearch';
import { useAuth } from 'context/AuthContext';

export default function useSuggestedProducts({
  product,
}: {
  product: ProductDocument;
}) {
  const {
    data: { data: userProfile },
  } = useUserProfile();

  const { data: basedOnActivity } = useQuery({
    queryKey: ['basedOnActivity', userProfile?.uid, product.id],
    queryFn: async () => {
      // update firebase types to remove as any
      const vecQuery = `embedding:([${(
        userProfile?.activity_profile as any
      ).value.toString()}], k:100)`;

      let catIndex = '';
      let catValue = product.category;
      if (product.category3) {
        catIndex = '3';
        catValue = product.category3;
      } else if (product.category2) {
        catIndex = '2';
        catValue = product.category2;
      } else if (product.category1) {
        catIndex = '1';
        catValue = product.category1;
      }
      console.log(`category${catIndex}:!="${catValue}"`);
      let searchRequests = {
        searches: [
          {
            collection: 'products_alias',
            q: '*',
            vector_query: vecQuery,
            filter_by: `category${catIndex}:!="${catValue}"`,
            group_by: 'seller_id',
            group_limit: 1,
          },
        ],
      };

      const { results } = await client.multiSearch.perform<ProductDocument[]>(
        searchRequests,
        {
          exclude_fields: 'shipment',
        }
      );
      return (
        results[0].grouped_hits
          ?.map((h) => h.hits?.map((hh) => hh.document as ProductDocument))
          .flat()
          .slice(0, 10) ?? []
      );
    },
    enabled: !!userProfile?.uid,
    throwOnError: false,
  });

  const { userDoc } = useAuth();
  const youMayAlsoLike = useQuery({
    queryKey: ['youMayAlsoLike', userDoc?.uid, product.id],
    queryFn: async () => {
      let bike_filter = '';
      if (product.category === 'Bike Parts') {
        if (product.fitting_bikes?.bikes?.length) {
          const uniqueMakes = new Set(
            product.fitting_bikes.bikes.map((b) => b.make)
          );
          const uniqueModels = new Set(
            product.fitting_bikes.bikes.map((b) => b.model)
          );
          if (uniqueModels.size) {
            const str = [...uniqueModels.values()]
              .map((m) => `\`${m}\``)
              .join(',');
            bike_filter += `(fitting_bikes_strings:[${str}])`;
          } else if (uniqueMakes.size) {
            const str = [...uniqueMakes.values()]
              .map((m) => `\`${m}\``)
              .join(',');
            bike_filter += `(fitting_bikes_strings:[${str}])`;
          }
        } else if (userDoc?.bikes?.length) {
          const str = userDoc.bikes.map((b) => `\`${b.model}\``).join(',');
          bike_filter += `(fitting_bikes_strings:[${str}])`;
        }
      }
      console.log(bike_filter);
      const searchRequests = {
        searches: [
          {
            collection: 'products_alias',
            q: '*',
            filter_by: `${bike_filter}`,
            per_page: 20,
            sort_by: 'popularity:desc',
          },
        ] as MultiSearchRequestSchema[],
      };
      const { results } = await client.multiSearch.perform<ProductDocument[]>(
        searchRequests,
        {
          exclude_fields: 'shipment',
        }
      );
      return {
        results:
          results[0].hits?.map((h) => h.document as ProductDocument) ?? [],
      };
    },
    enabled: !!product.id,
  });

  return {
    basedOnActivity,
    youMayAlsoLike,
  };
}
